import type {
  Price,
  InviaType,
  Room,
  DiscountType,
  PaymentMethod,
} from '@/redux/v1/models';
import type {
  CancellationRules,
  Flight,
  PricePax,
  RawCancellationRules,
  TourOperator,
  TourOperatorSpecials,
  TravelDateInfo,
} from '@/redux/v1/offers/list/models';

// backend
// -------

export const VACANCY_STATUS_IDLE = 'idle' as const;
export const VACANCY_STATUS_LOADING = 'loading' as const;
export const VACANCY_STATUS_ERROR = 'error' as const;

export const VACANCY_STATUS_AVAILABLE = 'available' as const;
export const VACANCY_STATUS_ON_REQUEST = 'on_request' as const;
export const VACANCY_STATUS_UNKNOWN = 'unknown' as const;
export const VACANCY_STATUS_SOLD_OUT = 'sold_out' as const;

export type VacancyStatus =
  | typeof VACANCY_STATUS_IDLE
  | typeof VACANCY_STATUS_LOADING
  | typeof VACANCY_STATUS_AVAILABLE
  | typeof VACANCY_STATUS_ERROR
  | typeof VACANCY_STATUS_ON_REQUEST
  | typeof VACANCY_STATUS_UNKNOWN
  | typeof VACANCY_STATUS_SOLD_OUT;

type RawPaymentInfoItem = {
  /**
   * @deprecated use tmpPrice instead
   */
  price: number;
  tmpPrice: Price;
  priceDisplay: Price;
  date?: null | string;
};

type RawPaymentInfo = {
  deposit?: RawPaymentInfoItem;
  final?: RawPaymentInfoItem;
};

export type RawAvailability = {
  // shared with Offer
  id: string;
  travelDateInfo: TravelDateInfo;
  tourOperator: TourOperator;
  rooms: Array<Room>;
  prices: Array<PricePax>;
  /**
   * @deprecated use tmpTotalPrice instead
   */
  totalPrice: PricePax;
  tmpTotalPrice: Price;
  totalPriceDisplay: Price;
  /**
   * @deprecated use tmpTotalPriceOriginal instead
   */
  totalPriceOriginal: number;
  tmpTotalPriceOriginal: Price;
  totalPriceOriginalDisplay: Price;
  /**
   * @deprecated use tmpTotalDiscount instead
   */
  totalDiscount: number;
  tmpTotalDiscount: Price;
  discountType: DiscountType;
  discountPercentage: number;
  totalDiscountDisplay: Price;
  supplier: string;
  transportation: null | undefined | Array<InviaType>;
  flight: Flight;
  tourOperatorSpecials?: TourOperatorSpecials;
  cancellationRules: RawCancellationRules;
  // specific to Availability
  bookingUrl: string;
  payments: Array<PaymentMethod> | null;
  paymentInfo?: RawPaymentInfo;
  tourOperatorInfo?: string;
};

// frontend
// --------

type PaymentInfoItem = {
  price: Price;
  priceDisplay: Price;
  date: null | string;
};

export type PaymentInfo = {
  deposit: PaymentInfoItem | null;
  final: PaymentInfoItem | null;
};

export type Availability = {
  // shared with Offer
  id: string;
  travelDateInfo: TravelDateInfo;
  tourOperator: TourOperator;
  rooms: Array<Room>;
  prices: Array<PricePax>;
  totalPrice: Price;
  totalPriceDisplay: Price;
  totalPriceOriginal: Price;
  totalPriceOriginalDisplay: Price;
  totalDiscount: Price;
  totalDiscountDisplay: Price;
  supplier: string;
  transportation: null | undefined | Array<InviaType>;
  flight: Flight;
  tourOperatorSpecials: TourOperatorSpecials;
  cancellationRules: CancellationRules;
  // specific to Availability
  bookingUrl: string;
  payments: Array<PaymentMethod>;
  paymentInfo: PaymentInfo;
  tourOperatorInfo?: string;
  discountType: DiscountType;
  discountPercentage: number;
};

export type RawAvailabilityResponse = {
  message: string;
  offer?: RawAvailability;
  status:
    | typeof VACANCY_STATUS_AVAILABLE
    | typeof VACANCY_STATUS_ON_REQUEST
    | typeof VACANCY_STATUS_SOLD_OUT
    | typeof VACANCY_STATUS_UNKNOWN;
};

type AvailabilityResponse = {
  message: string;
  offer?: Availability;
};

// state
// -----

export type AvailabilityState = {
  byOfferId: Record<string, AvailabilityResponse>;
  meta: Record<
    string,
    {
      fetchedAt: string;
      autoVacancy: boolean;
    }
  >;
  errors: Record<string, Error>;
  statusByOfferId: Record<string, VacancyStatus>;
};
